<template>
  <div class="w-full">
    <LcButton
      :aria-haspopup="playSlipValid ? 'false' : 'dialog'"
      class="button w-full"
      icon="arrow-right"
      icon-position="right"
      no-padding
      size="large"
      @click="redirectToCheckout"
    >
      {{ $t('common.words.checkout') }}
    </LcButton>

    <CheckoutRestrictions
      v-if="restrictionsError"
      v-bind="{ ...restrictionsError }"
      close-all-modals-on-confirm
    />
  </div>
</template>

<script lang="ts" setup>
import Money from '~~/models/Money'

import {
  KnownProductType,
  type Money as MoneyValue,
  type Restrictions,
  Status,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import {
  AccountVerificationModalControl,
  HighFrequencyGameIncompletePlaysModal,
  HighFrequencyGameMaxPriceModal,
} from '#components'

type Emits = {
  (e: 'use-quickpick'): void
}

const emit = defineEmits<Emits>()

const { navigateToLocalized } = useRouterUtils()
const localePath = useLocalePath()
const { openModal, registerModal } = useModal()

const {
  config,
  currentPrice,
  disclaimerAcceptedConfig,
  disclaimerAcceptedCookie,
  disclaimerAcceptedError,
  gameId,
  isQuickPick,
  playSlipValid,
  selectedAddOns,
  selectedQuantity,
  selectedWager,
  storeHighFrequencyGameSlip,
} = useHighFrequencyGame()
const route = useRoute()
const { isLoggedIn } = useAuth()
const { pushAddToCartEvent, pushBeginCheckoutEvent } = useTracking()
const { createECommerceEventData } = useTrackingData()
const { logTrackingError } = useTrackingErrorLog()

const openAccountVerificationModal = () =>
  openModal({
    component: AccountVerificationModalControl,
    props: {
      redirect: localePath(
        `/checkout?p=${gameId.toLowerCase()}&backroute=/keno/${gameId.toLowerCase()}`,
      ),
    },
  })

const openIncompletePlaysModal = () =>
  openModal({
    component: HighFrequencyGameIncompletePlaysModal,
    onCancel: () => emit('use-quickpick'),
  })

const maxPriceLimit = ref<MoneyValue>()

const restrictionsError = ref<{
  balance: MoneyValue
  grossTotal?: MoneyValue
  restrictions: Restrictions
}>()

const closeMaxPriceModal = () => {
  maxPriceLimit.value = undefined
}

registerModal({
  component: HighFrequencyGameMaxPriceModal,
  onClose: closeMaxPriceModal,
  onConfirm: closeMaxPriceModal,
  priority: 10,
  props: () => ({
    limit: maxPriceLimit.value ?? new Money(),
  }),
  when: () => Boolean(maxPriceLimit.value),
})

const wagers = computed(() => config.value?.wagers || [])

const generateEventData = async () => {
  if (!config.value) return null
  return await createECommerceEventData({
    addOns: selectedAddOns.value,
    currentPrice: currentPrice.value,
    gameId: config.value.gameId,
    isQuickPick: isQuickPick.value,
    numberOfGames: selectedQuantity.value ?? 0,
    type: KnownProductType.HighFrequencyGame,
    wager:
      wagers.value.find((wager) => wager.name === selectedWager.value)?.price.amount.value ?? 0,
  })
}

const redirectToCheckout = async () => {
  if (!playSlipValid.value) {
    openIncompletePlaysModal()
    return
  }

  if (!disclaimerAcceptedConfig.value && !disclaimerAcceptedCookie.value?.value) {
    disclaimerAcceptedError.value = true
    return
  }

  if (!isLoggedIn.value) {
    return await navigateToLocalized(
      createRedirectLocation(
        {
          path: '/login',
        },
        encodeRedirectPathWithQuery('/checkout/redirect', {
          p: gameId.toLowerCase(),
          // backroute needs to be last to work properly
          backroute: route.path,
        }),
      ),
      { replace: true },
    )
  }

  if (isLoggedIn.value) {
    const player = await useUserDataLazyQuery().load()
    if (player && player.player.status !== Status.Verified) {
      openAccountVerificationModal()
      return
    }

    const result = await storeHighFrequencyGameSlip()

    if (result.type === 'ticket-limit-exceeded') {
      maxPriceLimit.value = result.limit
      return
    }

    if (result.type === 'player-restricted') {
      restrictionsError.value = {
        balance: result.balance,
        grossTotal: result.grossTotal,
        restrictions: result.restrictions,
      }
      return
    }

    if (result.type !== 'stored') return
  }

  try {
    pushAddToCartEvent(await generateEventData())
    pushAddToCartEvent(await generateEventData(), true)
  } catch (err) {
    logTrackingError('add_to_cart_by_next_step', err)
  }

  try {
    pushBeginCheckoutEvent(await generateEventData())
  } catch (err) {
    logTrackingError('begin_checkout', err)
  }

  await navigateToLocalized({
    path: '/checkout',
    query: {
      p: gameId.toLowerCase(),
    },
  })
}

defineExpose({
  redirectToCheckout,
})
</script>
